import { ChangeEvent, useCallback, useState } from 'react';
import { noop } from '@cbhq/cds-utils';
import { Button } from '@cbhq/cds-web/buttons';
import { NativeTextArea, TextInput } from '@cbhq/cds-web/controls';
import { useBreakpoints } from '@cbhq/cds-web/hooks/useBreakpoints';
import { VStack } from '@cbhq/cds-web/layout';
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@cbhq/cds-web/overlays';

import useJSONValidation from ':cloud/hooks/useJSONValidation';
import { CoinbaseSmartContract } from ':cloud/types/ts_types';
import { ContentRow } from ':cloud/widgets/sharedcomponents/ContentRow';

import { getNetwork } from '../helpers';

type CoinbaseSmartContractEditFieldsModalProps = {
  smartContract: CoinbaseSmartContract;
  onRequestClose: () => void;
  onSubmit: (updatedWebhook: CoinbaseSmartContract) => void;
};

export function CoinbaseSmartContractEditFieldsModal({
  smartContract,
  onRequestClose,
  onSubmit,
}: Readonly<CoinbaseSmartContractEditFieldsModalProps>) {
  const [contractName, setContractName] = useState(smartContract.contractName);
  const [abi, setAbi] = useState(smartContract.abi);
  const { isPhone } = useBreakpoints();

  const {
    validateJSON,
    jsonValidationError: contractAbiError,
    clearJSONValidationError,
  } = useJSONValidation();

  const handleOnChangeContractName = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const name = event.target.value;
      setContractName(name);
    },
    [setContractName],
  );

  const handleOnChangeAbi = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      const newAbi = event.target.value.trim();
      setAbi(newAbi);
      if (contractAbiError) {
        clearJSONValidationError();
      }
    },
    [contractAbiError, clearJSONValidationError],
  );

  const handleOnBlurContractAbi = useCallback(() => {
    validateJSON(abi);
  }, [validateJSON, abi]);

  const handleSubmit = useCallback(() => {
    validateJSON(abi);

    if (contractAbiError) {
      return;
    }

    const updatedSmartContract: CoinbaseSmartContract = {
      ...smartContract,
      contractName: contractName.trim(),
      abi,
    };

    onSubmit(updatedSmartContract);
  }, [validateJSON, abi, contractAbiError, smartContract, contractName, onSubmit]);

  return (
    <Modal onRequestClose={onRequestClose || noop} visible width={isPhone ? undefined : 480}>
      <ModalHeader title="Edit Smart Contract Details" />
      <ModalBody>
        <VStack gap={2}>
          <TextInput
            label="Contract name"
            required
            placeholder="Contract name"
            value={contractName}
            onChange={handleOnChangeContractName}
          />
          <ContentRow description="Contract address" detail={smartContract.contractAddress} />
          <ContentRow description="Network" detail={getNetwork(smartContract.networkId)} />
          <TextInput
            inputNode={
              <NativeTextArea
                value={abi}
                onChange={handleOnChangeAbi}
                rows={7}
                cols={5}
                placeholder="Enter the smart contract ABI here"
              />
            }
            helperText={contractAbiError}
            onBlur={handleOnBlurContractAbi}
            label="Contract ABI"
            variant={contractAbiError ? 'negative' : undefined}
          />
        </VStack>
      </ModalBody>
      <ModalFooter
        secondaryAction={
          <Button variant="secondary" onPress={onRequestClose}>
            Cancel
          </Button>
        }
        primaryAction={
          <Button
            type="submit"
            onPress={handleSubmit}
            testID="submit"
            disabled={!contractName || !abi || !!contractAbiError}
          >
            Save
          </Button>
        }
      />
    </Modal>
  );
}
